import { gold } from '@ant-design/colors'
import { faArrowRight, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Flex, Modal, Space } from 'antd'
import VSpace from '../../../common/VSpace'
import PricingTypeDisplay from '../../PricingTypeDisplay'
import { WorkareaGroupMoveWarnings } from './workareaGroupsListModel'

type Props = {
  isOpen: boolean
  onCancel: () => void
  onMove: () => void
  warnings?: WorkareaGroupMoveWarnings
}

const MoveWorkareaWarningModal = ({ isOpen, onCancel, onMove, warnings }: Props) => {
  const getTitleAndButtonText = () => {
    if (warnings?.originalGroup.isApproved || warnings?.destinationGroup?.isApproved) {
      return {
        title: 'Workarea Cannot Be Moved',
        buttonText: 'Move Workarea',
        buttonDisabled: true,
      }
    }

    if (warnings?.originalGroupHasInvoices || warnings?.destinationGroupHasInvoices) {
      return {
        title: 'Workarea Cannot Be Moved',
        buttonText: 'Move Workarea',
        buttonDisabled: true,
      }
    }

    if (warnings?.pricingTypeWarning && warnings?.recurringWorkareaCount > 1) {
      return {
        title: 'Pricing Type Conflict',
        buttonText: 'Move All & Update Pricing Type',
      }
    }

    if ((warnings?.recurringWorkareaCount ?? 0) > 1) {
      return {
        title: 'Move Recurring Workareas',
        buttonText: 'Move All',
      }
    }
    
    return {
      title: 'Pricing Type Conflict',
      buttonText: 'Update Pricing Type',
    }
  }

  const titleAndButtonText = getTitleAndButtonText()

  return <Modal
    open={isOpen}
    onCancel={onCancel}
    title={
      <Space>
        <FontAwesomeIcon icon={faExclamationCircle} color={gold.primary} />
        {titleAndButtonText.title}
      </Space>
    }
    okText={titleAndButtonText.buttonText}
    onOk={onMove}
    okButtonProps={{ disabled: titleAndButtonText.buttonDisabled }}
  >
    <VSpace size='large'>
      {titleAndButtonText.buttonDisabled ? <NotMoveableWarnings warnings={warnings} /> : <StandardWarnings warnings={warnings} />}
    </VSpace>
  </Modal>
}

export default MoveWorkareaWarningModal

const StandardWarnings = ({warnings}: {warnings?: WorkareaGroupMoveWarnings}) => {
  return <>
    {warnings?.pricingTypeWarning && <VSpace>
      <strong>Update Pricing Type?</strong>
      <span>Groups may only contain workareas of the same pricing type.</span>
      <Flex gap={70} align='center'>
        <Space direction='vertical'>
          <strong>Current</strong>
          <PricingTypeDisplay pricingType={warnings.originalGroup.pricingType} />
        </Space>
        <FontAwesomeIcon icon={faArrowRight} />
        <Space direction='vertical'>
          <strong>New Pricing Type</strong>
          <PricingTypeDisplay pricingType={warnings.destinationGroup!.pricingType} />
        </Space>
      </Flex>
    </VSpace>}
    {(warnings?.recurringWorkareaCount ?? 0) > 1 && <VSpace>
      <strong>Move all {warnings?.recurringWorkareaCount} recurring workareas?</strong>
      <span>All related recurring workareas must be moved together.</span>
    </VSpace>}
  </>
}

const NotMoveableWarnings = ({warnings}: {warnings?: WorkareaGroupMoveWarnings}) => {
  const showApprovedWarnings = warnings?.originalGroup.isApproved || warnings?.destinationGroup?.isApproved
  const showInvoiceWarnings = warnings?.originalGroupHasInvoices || warnings?.destinationGroupHasInvoices
  return <>
    {showApprovedWarnings && <VSpace>
      <span>Workareas cannot be moved when they are approved or are moving in or out of an approved group:</span>
      <ul>
        {warnings?.originalGroup.isApproved && <li>{warnings.originalGroup.name} is approved.</li>}
        {warnings?.destinationGroup?.isApproved && <li>{warnings.destinationGroup.name} is approved.</li>}
      </ul>
    </VSpace>}
    {showInvoiceWarnings && <VSpace>
      <span>Workareas cannot be moved when there are associated invoices:</span>
      <ul>
        {warnings?.originalGroupHasInvoices && <li>{warnings.originalGroup.name} has already been invoiced.</li>}
        {warnings?.destinationGroupHasInvoices && <li>{warnings.destinationGroup?.name} has already been invoiced.</li>}
      </ul>
    </VSpace>}
  </>
}
